/**
 * Draws one frame of the Dale dot on a 2D canvas. The sphere is lit from the
 * top left and shaded the way a stipple drawing is: on paper the shadow side
 * gets more dots, on a dark page the lit side does. Dots behind the sphere
 * stay faintly visible, which is what makes it read as 3D and not as a disc.
 */

export type OrbFrame = {
  readonly sizePx: number;
  readonly dpr: number;
  readonly from: Float32Array;
  readonly to: Float32Array;
  /** 0 shows `from`, 1 shows `to`. */
  readonly morph: number;
  /** 0 keeps every dot and shades by tone; 1 thins the lit side into sparse stipple. */
  readonly stipple: number;
  /** Per point: where it drifts when the orb dissolves, and when it twinkles. */
  readonly drift: Float32Array;
  readonly thresholds: Float32Array;
  readonly phases: Float32Array;
  readonly rotation: number;
  readonly tilt: number;
  readonly scatter: number;
  readonly breathe: number;
  /** Typing energy, 0 to 1: a wave runs up from where the text box is. */
  readonly ripple: number;
  /** Speech level, 0 to 1: bands of dots swell as it talks. */
  readonly voice: number;
  readonly timeS: number;
  /** Dot colors from the lit side to the shadow side, any CSS color. */
  readonly inks: readonly string[];
  /** On a dark page density means light, so the stipple flips. */
  readonly onDark: boolean;
};

export type OrbCanvas = Pick<
  CanvasRenderingContext2D,
  'setTransform' | 'clearRect' | 'beginPath' | 'arc' | 'fill' | 'fillStyle' | 'globalAlpha'
>;

const RADIUS_SHARE = 0.31;
/** A tile has no dust cloud to make room for, so its sphere fills more of it. */
const SMALL_ORB_PX = 80;
const SMALL_RADIUS_SHARE = 0.35;
const CAMERA = 5;
/** Dust past this share of the canvas fades out instead of being cut by its square edge. */
const EDGE_FADE_START = 0.4;
const EDGE_FADE_END = 0.49;
const LIGHT = normalize(-0.55, -0.65, 0.52);
/** The text box sits below the orb, so typing ripples start at its bottom front. */
const INPUT_POLE = normalize(0, 0.75, 0.66);
const MIN_ALPHA = 0.03;
const FALLBACK_INK = '#e27a3c';

export function orbDotRadius(sizePx: number): number {
  if (sizePx >= 140) return 1.35;
  if (sizePx >= 72) return 1.1;
  return 1.05;
}

/** Small orbs have few dots, so each one carries more ink. */
function inkBoost(sizePx: number): number {
  return sizePx < 72 ? 1.6 : 1.1;
}

export function renderOrbFrame(ctx: OrbCanvas, frame: OrbFrame): void {
  const { sizePx, dpr } = frame;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, sizePx, sizePx);
  const center = sizePx / 2;
  const radius = sizePx * (sizePx < SMALL_ORB_PX ? SMALL_RADIUS_SHARE : RADIUS_SHARE);
  const dot = orbDotRadius(sizePx);
  const boost = inkBoost(sizePx);
  const count = frame.from.length / 3;
  const rotation = { cos: Math.cos(frame.rotation), sin: Math.sin(frame.rotation) };
  const tilt = { cos: Math.cos(frame.tilt), sin: Math.sin(frame.tilt) };

  for (let index = 0; index < count; index += 1) {
    const point = displacedPoint(frame, index);
    // Spin around the vertical axis, then tip the axis toward the viewer.
    const x1 = point[0] * rotation.cos + point[2] * rotation.sin;
    const z1 = -point[0] * rotation.sin + point[2] * rotation.cos;
    const y2 = point[1] * tilt.cos - z1 * tilt.sin;
    const z2 = point[1] * tilt.sin + z1 * tilt.cos;

    const shade = dotShade(frame, index, x1, y2, z2);
    const alpha = Math.min(1, shade.alpha * boost);
    if (alpha < MIN_ALPHA) continue;
    const perspective = CAMERA / (CAMERA - z2);
    const px = x1 * radius * perspective;
    const py = y2 * radius * perspective;
    const edge = 1 - smoothstep(EDGE_FADE_START, EDGE_FADE_END, Math.hypot(px, py) / sizePx);
    if (edge <= 0) continue;
    ctx.globalAlpha = alpha * edge;
    ctx.fillStyle = inkFor(frame, shade.density);
    ctx.beginPath();
    ctx.arc(center + px, center + py, dot * shade.size, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

/** The point after the morph, the breath, the voice, the typing ripple and the dissolve. */
function displacedPoint(frame: OrbFrame, index: number): [number, number, number] {
  const offset = index * 3;
  const base = morphPoint(frame.from, frame.to, offset, frame.morph);
  const length = Math.hypot(base[0], base[1], base[2]) || 1;
  const unit = [base[0] / length, base[1] / length, base[2] / length] as const;
  const t = frame.timeS;
  const phase = at(frame.phases, index);

  let scale = 1 + frame.breathe * Math.sin(t * 1.6);
  scale += frame.voice * 0.085 * (0.55 + 0.45 * Math.sin(unit[1] * 7 - t * 11));
  if (frame.ripple > 0) {
    const toward = unit[0] * INPUT_POLE[0] + unit[1] * INPUT_POLE[1] + unit[2] * INPUT_POLE[2];
    scale += frame.ripple * 0.07 * Math.sin(toward * 9 - t * 10) * (0.5 + 0.5 * toward);
  }

  // Dissolving pushes each dot out along its own direction, plus some noise.
  // It breaks from the right first and closes from the left last, so the
  // sphere sweeps into a drifting shell of dust instead of popping.
  const order = 0.5 + 0.5 * unit[0];
  const local = Math.min(1, Math.max(0, frame.scatter * 1.6 - (1 - order) * 0.6));
  const drift = local * (0.7 + 0.3 * Math.sin(t * 1.3 + phase));
  const outward = 0.24 * drift;
  const noise = 0.22 * drift;
  return [
    base[0] * scale + unit[0] * outward + at(frame.drift, offset) * noise,
    base[1] * scale + unit[1] * outward + at(frame.drift, offset + 1) * noise,
    base[2] * scale + unit[2] * outward + at(frame.drift, offset + 2) * noise,
  ];
}

/** Travels each point along the surface rather than through the middle of the sphere. */
function morphPoint(from: Float32Array, to: Float32Array, offset: number, morph: number): [number, number, number] {
  const ax = at(from, offset);
  const ay = at(from, offset + 1);
  const az = at(from, offset + 2);
  if (morph <= 0) return [ax, ay, az];
  const bx = at(to, offset);
  const by = at(to, offset + 1);
  const bz = at(to, offset + 2);
  if (morph >= 1) return [bx, by, bz];
  const x = ax + (bx - ax) * morph;
  const y = ay + (by - ay) * morph;
  const z = az + (bz - az) * morph;
  const mixed = Math.hypot(x, y, z);
  if (mixed < 1e-4) return [bx, by, bz];
  const wanted = Math.hypot(ax, ay, az) * (1 - morph) + Math.hypot(bx, by, bz) * morph;
  return [(x / mixed) * wanted, (y / mixed) * wanted, (z / mixed) * wanted];
}

/**
 * How strongly a dot is inked and how big it is. The shadow side gets darker,
 * slightly larger dots; with stipple on, the lit side also loses dots.
 */
function dotShade(
  frame: OrbFrame,
  index: number,
  x: number,
  y: number,
  z: number,
): { alpha: number; size: number; density: number } {
  const length = Math.hypot(x, y, z) || 1;
  const lambert = (x * LIGHT[0] + y * LIGHT[1] + z * LIGHT[2]) / length;
  const density = frame.onDark ? 0.5 + 0.5 * lambert : 0.5 - 0.5 * lambert;
  const threshold = at(frame.thresholds, index);
  const stippled = smoothstep(threshold - 0.14, threshold + 0.14, 0.12 + 0.88 * density);
  const kept = 1 - frame.stipple * (1 - stippled);
  const tone = 0.5 + 0.5 * density;
  // A dissolving orb shows all its dust evenly; a whole one shows its shading.
  const shown = kept * tone + (0.62 - kept * tone) * frame.scatter;
  const front = clamp01((z + 1.4) / 2.8);
  const twinkle = 1 - 0.16 * frame.stipple * (0.5 + 0.5 * Math.sin(frame.timeS * 2.1 + at(frame.phases, index) * 3));
  return {
    alpha: shown * (0.2 + 0.8 * front ** 1.4) * twinkle,
    size: (0.6 + 0.55 * front) * (0.82 + 0.3 * density),
    density,
  };
}

/** On paper dense means shadow, so it takes the deep orange; on a dark page dense means light, the bright one. */
function inkFor(frame: OrbFrame, density: number): string {
  const last = frame.inks.length - 1;
  const step = Math.min(last, Math.floor(density * frame.inks.length));
  return frame.inks[frame.onDark ? last - step : step] ?? FALLBACK_INK;
}

/** Typed arrays read past their end as 0; this says so to the type checker without a branch per call site. */
function at(values: Float32Array, index: number): number {
  return values[index] ?? 0;
}

function smoothstep(edge0: number, edge1: number, value: number): number {
  const t = clamp01((value - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function normalize(x: number, y: number, z: number): readonly [number, number, number] {
  const length = Math.hypot(x, y, z);
  return [x / length, y / length, z / length];
}
